/**
 * CDM Correlations — Track 3.11 cross-domain correlation engine.
 *
 * The intelligence-layer moat: joins cdm_events × threat_actors × CVEs ×
 * ransomware_victims × articles × frontier_events × incidents into single
 * correlation findings. Six day-one rules ship pre-seeded; admins can
 * trigger any rule on demand and decide each finding (action / dismiss /
 * triage / auto-resolve).
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Network, RefreshCw, ChevronLeft, AlertTriangle, Activity, X,
  CheckCircle2, XCircle, Clock, Sparkles,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface CorrelationRow {
  id: string
  rule_id: string
  rule_name: string | null
  display_name: string | null
  org_id: string
  opened_at: string | null
  window_start: string | null
  window_end: string | null
  contributing_event_count: number
  shared_entities: Record<string, unknown>
  score: number
  severity: string
  state: string
  actor_summary: string
  agentic_reasoning: Record<string, unknown> | null
  acked_by: string | null
  acked_at: string | null
  acked_notes: string | null
}

interface CorrelationDetail extends CorrelationRow {
  rule_kind: string
  rule_dsl: string
  contributing_events: Array<{
    id: string; kind: string; source: string; archetype: string
    field: string | null; canonical_value: unknown; entities: unknown
    observed_at: string | null
  }>
}

interface RuleRow {
  id: string
  org_id: string | null
  name: string
  display_name: string
  dsl: string
  rule_kind: string
  config: Record<string, unknown>
  window_seconds: number
  min_co_occurrence: number
  min_severity: string | null
  action: string
  enabled: boolean
  version: number
  last_evaluated_at: string | null
  last_match_count: number
}

const SEVERITY_TONE: Record<string, string> = {
  critical: 'bg-rose/10 text-rose-light border-rose/30',
  high:     'bg-amber/10 text-amber-light border-amber/30',
  medium:   'bg-cyan/10 text-cyan border-cyan/30',
  low:      'bg-emerald/10 text-emerald border-emerald/30',
}

const STATE_TONE: Record<string, string> = {
  open:           'bg-rose/10 text-rose-light border-rose/30',
  triaging:       'bg-amber/10 text-amber-light border-amber/30',
  actioned:       'bg-emerald/10 text-emerald border-emerald/30',
  dismissed:      'bg-text-muted/10 text-text-muted border-border',
  auto_resolved:  'bg-text-muted/10 text-text-muted border-border',
}

const STATES = ['open', 'triaging', 'actioned', 'dismissed', 'auto_resolved'] as const
const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const

interface ListResp {
  count: number
  tiles: { open: number; critical_24h: number; high_24h: number; total_24h: number }
  rows: CorrelationRow[]
}

export default function CdmCorrelationsPage() {
  const [data, setData] = useState<ListResp | null>(null)
  const [rules, setRules] = useState<RuleRow[]>([])
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [message, setMessage] = useState('')
  const [stateFilter, setStateFilter] = useState<string>('open')
  const [severityFilter, setSeverityFilter] = useState<string>('')
  const [ruleFilter, setRuleFilter] = useState<string>('')
  const [drawer, setDrawer] = useState<CorrelationDetail | null>(null)

  const load = async () => {
    setLoading(true)
    const params: Record<string, string | number> = { limit: 200 }
    if (stateFilter) params.state = stateFilter
    if (severityFilter) params.severity = severityFilter
    if (ruleFilter) params.rule = ruleFilter
    try {
      const r = await api.get('/api/cdm/correlations', { params })
      setData(r.data as ListResp)
    } catch {
      // ignore — show empty state
    } finally {
      setLoading(false)
    }
  }

  const loadRules = async () => {
    try {
      const r = await api.get('/api/cdm/correlation-rules')
      setRules((r.data?.rules || []) as RuleRow[])
    } catch {
      setRules([])
    }
  }

  useEffect(() => { void load() }, [stateFilter, severityFilter, ruleFilter])
  useEffect(() => { void loadRules() }, [])

  const runAll = async () => {
    setRunning(true)
    setMessage('Evaluating all rules — Planner → Critic agentic ratification…')
    try {
      const r = await api.post('/api/cdm/correlations/run-all')
      const s = r.data as { inserted: number; deduped: number; rules_evaluated: number }
      setMessage(`Done. ${s.rules_evaluated} rules → ${s.inserted} new, ${s.deduped} deduped.`)
      await load()
      await loadRules()
    } catch {
      setMessage('Run failed — admin permission required, or no rules enabled')
    } finally {
      setRunning(false)
    }
  }

  const openDrawer = async (id: string) => {
    try {
      const r = await api.get(`/api/cdm/correlations/${id}`)
      setDrawer(r.data as CorrelationDetail)
    } catch {
      setDrawer(null)
    }
  }

  const decide = async (decision: 'actioned' | 'dismissed' | 'auto_resolve' | 'triaging') => {
    if (!drawer) return
    try {
      await api.post(`/api/cdm/correlations/${drawer.id}/decide`, { decision })
      setDrawer(null)
      await load()
    } catch {
      setMessage('Decide failed — admin permission required')
    }
  }

  const ruleOptions = useMemo(() => rules.map(r => ({
    value: r.name, label: r.display_name,
  })), [rules])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Network size={18} className="text-cyan" /> CDM CORRELATIONS
        </h1>
        <span className="text-[10px] text-text-muted">
          Cross-domain joins · agentic ratification · {rules.filter(r => r.enabled).length} rules enabled
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/app/cdm/correlation-rules" className="btn-ghost">
            <Activity size={11} /> Manage rules
          </Link>
          <button type="button" onClick={runAll} disabled={running} className="btn-primary">
            <Sparkles size={11} className={running ? 'animate-spin' : ''} /> Run all rules
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="card p-2 text-[11px] text-text-secondary">{message}</div>
      )}

      {/* Hero tiles */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Open</div>
            <div className="text-2xl font-black font-mono mt-1 text-rose-light">{data.tiles.open}</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Critical 24h</div>
            <div className="text-2xl font-black font-mono mt-1 text-rose-light">{data.tiles.critical_24h}</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">High 24h</div>
            <div className="text-2xl font-black font-mono mt-1 text-amber-light">{data.tiles.high_24h}</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Total 24h</div>
            <div className="text-2xl font-black font-mono mt-1 text-text-primary">{data.tiles.total_24h}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-widest text-text-muted mr-1">State</span>
          {STATES.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setStateFilter(s === stateFilter ? '' : s)}
              className={clsx(
                'text-[10px] px-2 py-0.5 rounded border',
                s === stateFilter
                  ? STATE_TONE[s]
                  : 'border-border text-text-muted hover:text-text-primary',
              )}
            >
              {s.replace('_', ' ')}
            </button>
          ))}
        </div>
        <select className="input text-[11px]" value={severityFilter}
                onChange={e => setSeverityFilter(e.target.value)}
                aria-label="Severity filter">
          <option value="">All severities</option>
          {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="input text-[11px]" value={ruleFilter}
                onChange={e => setRuleFilter(e.target.value)}
                aria-label="Rule filter">
          <option value="">All rules</option>
          {ruleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>

      {/* Findings grid */}
      <div className="card p-3">
        {loading && <div className="text-text-muted text-xs">Loading…</div>}
        {!loading && (!data || data.rows.length === 0) && (
          <div className="text-text-muted text-xs flex items-center gap-2">
            <AlertTriangle size={14} /> No findings yet. Click <em>Run all rules</em> to evaluate now.
          </div>
        )}
        <div className="space-y-1">
          {data?.rows.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => void openDrawer(r.id)}
              className="w-full flex items-center gap-3 p-2 rounded border border-border bg-surface-card hover:bg-surface-raised text-left transition-colors"
            >
              <div className="flex-shrink-0 text-[10px] text-text-muted font-mono w-32 truncate">
                {r.opened_at ? new Date(r.opened_at).toLocaleString() : '—'}
              </div>
              <span className={clsx('text-[10px] px-2 py-0.5 rounded border w-20 text-center',
                                    SEVERITY_TONE[r.severity] || SEVERITY_TONE.medium)}>
                {r.severity}
              </span>
              <span className={clsx('text-[10px] px-2 py-0.5 rounded border w-24 text-center',
                                    STATE_TONE[r.state] || STATE_TONE.open)}>
                {r.state.replace('_', ' ')}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-text-primary font-medium truncate">{r.actor_summary || '(no summary)'}</div>
                <div className="text-[10px] text-text-muted truncate">
                  {r.display_name || r.rule_name} · {r.contributing_event_count} event(s) · score {r.score?.toFixed(2)}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Drawer */}
      {drawer && (
        <div
          className="fixed inset-0 z-40 bg-black/50 flex justify-end"
          onClick={() => setDrawer(null)}
        >
          <div
            className="w-full max-w-2xl h-full bg-surface-card border-l border-border overflow-y-auto p-4 animate-slide-in"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-text-primary flex items-center gap-2">
                <Network size={14} className="text-cyan" />
                {drawer.display_name || drawer.rule_name}
              </h2>
              <button type="button" onClick={() => setDrawer(null)} className="btn-ghost">
                <X size={12} /> Close
              </button>
            </div>
            <div className="text-[11px] text-text-secondary mb-2">{drawer.actor_summary}</div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={clsx('text-[10px] px-2 py-0.5 rounded border',
                                    SEVERITY_TONE[drawer.severity] || SEVERITY_TONE.medium)}>
                {drawer.severity}
              </span>
              <span className={clsx('text-[10px] px-2 py-0.5 rounded border',
                                    STATE_TONE[drawer.state] || STATE_TONE.open)}>
                {drawer.state.replace('_', ' ')}
              </span>
              <span className="text-[10px] text-text-muted">
                opened {drawer.opened_at ? new Date(drawer.opened_at).toLocaleString() : '—'}
              </span>
              <span className="text-[10px] text-text-muted">score {drawer.score?.toFixed(2)}</span>
            </div>

            <div className="card p-2 mb-3 bg-surface-raised">
              <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Rule DSL</div>
              <code className="text-[10px] text-text-primary font-mono whitespace-pre-wrap break-all">
                {drawer.rule_dsl || drawer.rule_kind}
              </code>
            </div>

            <div className="card p-2 mb-3">
              <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Shared entities</div>
              <pre className="text-[10px] text-text-primary font-mono overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(drawer.shared_entities, null, 2)}
              </pre>
            </div>

            {drawer.agentic_reasoning && (
              <div className="card p-2 mb-3">
                <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1 flex items-center gap-1">
                  <Sparkles size={10} className="text-cyan" /> Agentic reasoning
                </div>
                <pre className="text-[10px] text-text-secondary font-mono overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(drawer.agentic_reasoning, null, 2)}
                </pre>
              </div>
            )}

            <div className="card p-2 mb-3">
              <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">
                Contributing events ({drawer.contributing_events.length})
              </div>
              <div className="space-y-1">
                {drawer.contributing_events.map(ev => (
                  <div key={ev.id} className="text-[10px] font-mono p-1.5 rounded bg-surface-raised border border-border">
                    <div className="flex items-center gap-2">
                      <Clock size={10} className="text-text-muted" />
                      <span className="text-text-muted">
                        {ev.observed_at ? new Date(ev.observed_at).toLocaleString() : '—'}
                      </span>
                      <span className="text-text-primary">{ev.archetype}</span>
                      <span className="text-text-muted">/{ev.field || '?'}</span>
                      <span className="text-cyan">@{ev.source}</span>
                    </div>
                    {ev.canonical_value != null && Object.keys((ev.canonical_value as Record<string, unknown>) || {}).length > 0 ? (
                      <div className="text-[10px] text-text-muted truncate">
                        {JSON.stringify(ev.canonical_value)}
                      </div>
                    ) : null}
                  </div>
                ))}
                {drawer.contributing_events.length === 0 && (
                  <div className="text-[10px] text-text-muted">No event rows resolved.</div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => decide('actioned')} className="btn-primary">
                <CheckCircle2 size={11} /> Action
              </button>
              <button type="button" onClick={() => decide('triaging')} className="btn-ghost">
                <Activity size={11} /> Triage
              </button>
              <button type="button" onClick={() => decide('dismissed')} className="btn-ghost">
                <XCircle size={11} /> Dismiss
              </button>
              <button type="button" onClick={() => decide('auto_resolve')} className="btn-ghost">
                <Clock size={11} /> Auto-resolve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
